import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Search,
  LayoutGrid,
  List,
  Eye,
  Edit,
  Trash2,
  Download,
  FileText,
  Info,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Loader,
  CheckCircle2,
  Mail,
  Phone as PhoneIcon,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { cn } from '@/lib/utils';
import { projectsAPI } from '@/services/projects';
import { quotationsAPI } from '@/services/quotations';
import { pipelineStatusesAPI } from '@/services/pipelineStatuses';

const ProjectsListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [projects, setProjects] = useState([]);
  const [latestQuotations, setLatestQuotations] = useState({});
  const [financialData, setFinancialData] = useState(null);
  const [view, setView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [pipelineStatuses, setPipelineStatuses] = useState([]);

  const projectStatusConfig = {
    Pending: {
      className: 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200',
      icon: Clock
    },
    'Under Process': {
      className: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
      icon: Loader
    },
    Complete: {
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
      icon: CheckCircle2
    }
  };

  const projectStatusOptions = Object.keys(projectStatusConfig);

  const getColorConfigByScheme = (colorScheme) => {
    const colorMap = {
      blue: { className: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' },
      orange: { className: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
      purple: { className: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100' },
      green: { className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
      red: { className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
      slate: { className: 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200' },
      amber: { className: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' }
    };
    return colorMap[colorScheme] || colorMap.blue;
  };

  const getIconByName = (iconName) => {
    const iconMap = {
      Info,
      Calendar,
      FileText,
      CheckCircle,
      XCircle,
      Clock,
      AlertCircle
    };
    return iconMap[iconName] || Info;
  };

  const getDefaultColorConfig = () => {
    return { className: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' };
  };

  const getStatusConfig = (statusTitle) => {
    const status = pipelineStatuses.find((s) => s.title === statusTitle);
    if (!status) return getDefaultColorConfig();
    return getColorConfigByScheme(status.color_scheme);
  };

  const getStatusIcon = (statusTitle) => {
    const status = pipelineStatuses.find((s) => s.title === statusTitle);
    if (!status) return Info;
    return getIconByName(status.icon_name);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const statusResponse = await pipelineStatusesAPI.list();
      if (statusResponse.success) {
        setPipelineStatuses(statusResponse.data || []);
      }

      await fetchProjects();
      await fetchFinancialSummary();
    } catch (error) {
      console.error('Failed to load initial data:', error);
      addNotification('Error', 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.list({ archived: false });
      if (response.success) {
        let fetchedProjects = response.data || [];

        fetchedProjects.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        fetchedProjects = fetchedProjects.map((p, i) => ({
          ...p,
          rowNumber: fetchedProjects.length - i,
          serialNumber: p.serial_number,
          projectStatus: p.project_status || 'Pending',
          projectName: p.project_name,
          companyName: p.company_name,
          contactEmail: p.contact_email,
          contactPhone: p.contact_phone,
          status: p.status || 'New Inquiry',
          createdAt: p.created_at || p.createdAt,
          latestQuotationNumber: p.latest_quotation_number,
          latestQuotationVersion: p.latest_quotation_version
        }));

        setProjects(fetchedProjects);
        setLatestQuotations({});
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      addNotification('Error', 'Failed to load projects', 'error');
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await projectsAPI.financialSummary();
      if (response.success) {
        setFinancialData(response.data);
      }
    } catch (error) {
      console.error('Failed to load financial summary:', error);
    }
  };

  const fetchAllQuotations = async (projectsList) => {
    const quotesMap = {};

    for (const project of projectsList) {
      try {
        const response = await quotationsAPI.list(project.id);
        if (response.success && response.data && response.data.length > 0) {
          const sorted = response.data.sort(
            (a, b) =>
              new Date(b.created_at || b.createdAt) -
              new Date(a.created_at || a.createdAt)
          );
          quotesMap[project.id] = sorted[0];
        }
      } catch (error) {
        console.warn(`Failed to load quotations for project ${project.id}:`, error);
      }
    }

    setLatestQuotations(quotesMap);
  };

  const handleArchive = async (id) => {
    try {
      await projectsAPI.archive(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      addNotification('Project Archived', 'Project has been moved to archives.');
    } catch (error) {
      addNotification('Error', 'Failed to archive project', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;

    try {
      await projectsAPI.delete(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      addNotification(
        'Project Deleted',
        'Project has been permanently deleted.',
        'warning'
      );
    } catch (error) {
      addNotification('Error', 'Failed to delete project', 'error');
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    const currentProject = projects.find((p) => p.id === projectId);
    if (!currentProject || currentProject.status === newStatus) return;

    try {
      await projectsAPI.updateStatus(projectId, newStatus);

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, status: newStatus, updated_at: new Date().toISOString() }
            : p
        )
      );

      addNotification(
        'Status Updated',
        `Project "${currentProject.projectName}" moved to ${newStatus}`,
        'success'
      );
    } catch (error) {
      console.error('Failed to update status:', error);
      addNotification('Error', 'Failed to update status', 'error');
    }
  };

  const handleProjectStatusChange = async (projectId, newStatus) => {
    const currentProject = projects.find((p) => p.id === projectId);
    if (!currentProject || currentProject.projectStatus === newStatus) return;

    try {
      await projectsAPI.updateProjectStatus(projectId, newStatus);

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, projectStatus: newStatus, updated_at: new Date().toISOString() }
            : p
        )
      );

      if (newStatus === 'Complete') {
        addNotification(
          'Project Completed',
          `Project "${currentProject.projectName}" marked as Complete!`,
          'success'
        );
      }
    } catch (error) {
      console.error('Failed to update project status:', error);
      addNotification('Error', 'Failed to update project status', 'error');
    }
  };

  const exportCSV = () => {
    const headers = [
      'Row No',
      'Serial',
      'Project Name',
      'Email',
      'Phone',
      'Company',
      'Quotation',
      'Status',
      'Project Status'
    ];

    const csvContent = [
      headers.join(','),
      ...projects.map((p) => {
        const quoteNum =
          p.latestQuotationNumber ||
          latestQuotations[p.id]?.quotation_number ||
          latestQuotations[p.id]?.quotationNumber ||
          'No Quotation';

        return `"${p.rowNumber || ''}","${p.serialNumber || ''}","${p.projectName}","${
          p.contactEmail || ''
        }","${p.contactPhone || ''}","${p.companyName}","${quoteNum}","${
          p.status
        }","${p.projectStatus || 'Pending'}"`;
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'projects_export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(value);
  };

  const filtered = projects.filter(
    (p) =>
      p.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.companyName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const PipelineStatusSelect = ({ value, onChange }) => {
    const config = getStatusConfig(value);
    const CurrentIcon = getStatusIcon(value);

    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className={cn(
            'w-full h-8 text-xs font-medium border',
            config.className
          )}
        >
          <div className="flex items-center gap-1.5">
            <CurrentIcon className="h-3.5 w-3.5" />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          {pipelineStatuses.map((status) => {
            const Icon = getIconByName(status.icon_name);
            return (
              <SelectItem key={status.id} value={status.title}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span>{status.title}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  const StatusSelect = ({ value, onChange, options, configMap }) => {
    const CurrentIcon = configMap[value]?.icon || Info;

    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className={cn(
            'w-full h-8 text-xs font-medium border',
            configMap[value]?.className
          )}
        >
          <div className="flex items-center gap-1.5">
            <CurrentIcon className="h-3.5 w-3.5" />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => {
            const Icon = configMap[opt]?.icon || Info;
            return (
              <SelectItem key={opt} value={opt}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span>{opt}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Projects | CRM</title>
      </Helmet>

      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Projects</h1>
            <p className="text-muted-foreground">Manage all your projects</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => navigate('/projects/add')}>
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </Button>
          </div>
        </div>

        {financialData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card
              className="border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate('/financial/income')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase">
                    Total Income
                  </span>
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-green-700 mb-2">
                  {formatCurrency(financialData.total_income)}
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-green-600">
                    In: {formatCurrency(financialData.total_income_in)}
                  </span>
                  <span className="text-red-600">
                    Out: {formatCurrency(financialData.total_income_out)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="border-l-4 border-l-red-500 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate('/financial/expenses')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase">
                    Total Expense
                  </span>
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
                <div className="text-3xl font-bold text-red-700 mb-2">
                  {formatCurrency(financialData.total_expenses)}
                </div>
                <p className="text-xs text-muted-foreground">
                  All approved expenses (including overhead costs)
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase">
                    Net Profit
                  </span>
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
                <div
                  className={cn(
                    'text-3xl font-bold mb-2',
                    parseFloat(financialData.net_profit) >= 0
                      ? 'text-blue-700'
                      : 'text-red-700'
                  )}
                >
                  {formatCurrency(financialData.net_profit)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Income - Expenses (Approved)
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-1 border rounded-md p-1">
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={view === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {view === 'list' ? (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 text-center">No.</TableHead>
                  <TableHead className="w-32">Created</TableHead>
                  <TableHead className="w-16 text-center">#</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Quotation</TableHead>
                  <TableHead>Pipeline Status</TableHead>
                  <TableHead>Project Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No projects found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((project) => {
                    const quote = latestQuotations[project.id];
                    const quotationNumber =
                      project.latestQuotationNumber ||
                      quote?.quotation_number ||
                      quote?.quotationNumber;
                    const quotationVersion =
                      project.latestQuotationVersion || quote?.version;

                    return (
                      <TableRow
                        key={project.id}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                      >
                        <TableCell className="text-center font-medium text-muted-foreground">
                          {project.rowNumber}
                        </TableCell>

                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {project.createdAt
                            ? new Date(project.createdAt).toLocaleDateString()
                            : '-'}
                        </TableCell>

                        <TableCell className="text-center font-medium">
                          {project.serialNumber || '-'}
                        </TableCell>

                        <TableCell>
                          <div>
                            <div className="font-medium">{project.projectName}</div>
                            <div className="text-sm text-muted-foreground">
                              {project.companyName}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="space-y-1 text-sm">
                            {project.contactEmail && (
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <Mail className="h-3.5 w-3.5" />
                                {project.contactEmail}
                              </div>
                            )}
                            {project.contactPhone && (
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <PhoneIcon className="h-3.5 w-3.5" />
                                {project.contactPhone}
                              </div>
                            )}
                          </div>
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {quotationNumber ? (
                            <div
                              className="text-sm cursor-pointer hover:bg-blue-50 p-1 rounded transition-colors"
                              onClick={() =>
                                navigate(`/projects/${project.id}/quotation`)
                              }
                            >
                              <div className="font-medium text-blue-600 hover:text-blue-800">
                                {quotationNumber}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {quotationVersion || 'R1'} - Click to view
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              No Quotation
                            </span>
                          )}
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <PipelineStatusSelect
                            value={project.status}
                            onChange={(newStatus) =>
                              handleStatusChange(project.id, newStatus)
                            }
                          />
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <StatusSelect
                            value={project.projectStatus}
                            onChange={(newStatus) =>
                              handleProjectStatusChange(project.id, newStatus)
                            }
                            options={projectStatusOptions}
                            configMap={projectStatusConfig}
                          />
                        </TableCell>

                        <TableCell
                          className="text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/projects/${project.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`/projects/${project.id}/edit`)
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchive(project.id)}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(project.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((project) => {
              const quote = latestQuotations[project.id];
              const quotationNumber =
                project.latestQuotationNumber ||
                quote?.quotation_number ||
                quote?.quotationNumber;
              const quotationVersion =
                project.latestQuotationVersion || quote?.version;

              const StatusIcon = getStatusIcon(project.status);
              const statusConfig = getStatusConfig(project.status);
              const ProjectStatusIcon =
                projectStatusConfig[project.projectStatus]?.icon || Clock;

              return (
                <Card
                  key={project.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">
                          {project.projectName}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {project.companyName}
                        </p>
                        {project.createdAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Created:{' '}
                            {new Date(project.createdAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">
                          No. {project.rowNumber}
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">
                          #{project.serialNumber}
                        </div>
                      </div>
                    </div>

                    <div
                      className="space-y-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Badge
                        className={cn(
                          'w-full justify-start gap-2',
                          statusConfig.className
                        )}
                      >
                        <StatusIcon className="h-3.5 w-3.5" />
                        {project.status}
                      </Badge>
                      <Badge
                        className={cn(
                          'w-full justify-start gap-2',
                          projectStatusConfig[project.projectStatus]?.className
                        )}
                      >
                        <ProjectStatusIcon className="h-3.5 w-3.5" />
                        {project.projectStatus}
                      </Badge>
                    </div>

                    {quotationNumber && (
                      <div
                        className="text-sm border-t pt-3 hover:bg-blue-50 p-2 -mx-2 rounded cursor-pointer transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project.id}/quotation`);
                        }}
                      >
                        <div className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                          <FileText className="h-4 w-4" />
                          <span className="font-medium">{quotationNumber}</span>
                          <span className="ml-auto text-xs">
                            {quotationVersion || 'R1'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click to view quotation
                        </p>
                      </div>
                    )}

                    {(project.contactEmail || project.contactPhone) && (
                      <div
                        className="space-y-1 text-sm border-t pt-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {project.contactEmail && (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="truncate">{project.contactEmail}</span>
                          </div>
                        )}
                        {project.contactPhone && (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <PhoneIcon className="h-3.5 w-3.5" />
                            {project.contactPhone}
                          </div>
                        )}
                      </div>
                    )}

                    <div
                      className="flex gap-2 pt-2 border-t"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/projects/${project.id}/edit`)}
                      >
                        <Edit className="h-3.5 w-3.5 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(project.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectsListPage;